import intersection from 'lodash/intersection'
import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import type {ManagedCountT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {identityProvidersWithCount} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {NO_IDENTITY_PROVIDER_VALUE} from '@freckle/school/ts/manage-teachers/api-helpers/filters'

export type SummaryForEntityTagT = 'for-teacher' | 'for-student' | 'for-course'

export type SummaryForEntityT = {
  tag: SummaryForEntityTagT
} & SummaryT

function managedCount(summary: SummaryForEntityT): ManagedCountT {
  switch (summary.tag) {
    case 'for-teacher':
      return summary.teacherCounts
    case 'for-student':
      return summary.studentCounts
    case 'for-course':
      return summary.courseCounts
    default:
      return exhaustive(summary.tag)
  }
}

export function teacherCreatedOrIdpDisconnectedCount(summary: SummaryForEntityT): number {
  switch (summary.tag) {
    case 'for-teacher':
      return (
        summary.teacherCounts.manual + summary.teacherCounts.unknown + summary.teacherCounts.idpSync
      )
    case 'for-student':
      return (
        summary.studentCounts.manual + summary.studentCounts.unknown + summary.studentCounts.idpSync
      )
    case 'for-course':
      return summary.courseCounts.teacher
    default:
      return exhaustive(summary.tag)
  }
}

export function getPossibleSources(summary: SummaryForEntityT) {
  return [
    ...identityProvidersWithCount(managedCount(summary)),
    ...(teacherCreatedOrIdpDisconnectedCount(summary) > 0 ? [NO_IDENTITY_PROVIDER_VALUE] : []),
  ]
}

function hasAnyIdp({classlink, clever, renaissance, upload}: ManagedCountT): boolean {
  return classlink > 0 || clever > 0 || renaissance > 0 || upload > 0
}

const removeNoIdpSourceForIdpSchool = (
  sources: Array<IdentityProviderOrNoneT>,
  summary: SummaryForEntityT,
) =>
  hasAnyIdp(managedCount(summary))
    ? sources.filter((s: IdentityProviderOrNoneT) => s !== NO_IDENTITY_PROVIDER_VALUE)
    : sources

export function getInitialSelection(
  mInitSources: Array<IdentityProviderOrNoneT> | undefined | null,
  possibleSources: Array<IdentityProviderOrNoneT>,
  summary: SummaryForEntityT,
) {
  return maybe(
    () => removeNoIdpSourceForIdpSchool(possibleSources, summary),
    initSources => {
      const overlap = intersection<IdentityProviderOrNoneT>(possibleSources, initSources)
      // Only remove no idp source when falling back to all possible sources
      return overlap.length > 0 ? overlap : removeNoIdpSourceForIdpSchool(possibleSources, summary)
    },
    mInitSources,
  )
}
